import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Trash2,
  Undo2,
  AlertTriangle,
  Loader2,
  Ticket,
  Gift,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const TrashItem = ({ item, type, onRestore, onPermanent }) => {
  const danger = (item.days_until_purge ?? 0) <= 10;
  return (
    <li className="bg-white border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`trash-item-${item.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold truncate">{item.title}</p>
        <p className="text-xs text-gray-500">Borrada el {formatDate(item.deleted_at)}</p>
        <p className={`text-xs font-medium mt-1 ${danger ? 'text-red-600' : 'text-amber-700'}`}>
          Se elimina permanentemente en <strong>{item.days_until_purge ?? '?'}</strong> {item.days_until_purge === 1 ? 'día' : 'días'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onRestore(type, item.id)}
          className="border-green-300 text-green-700 hover:bg-green-50"
          data-testid={`restore-${item.id}`}
        >
          <Undo2 size={14} className="mr-1" />
          Restaurar
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPermanent(type, item.id, item.title)}
          className="border-red-300 text-red-700 hover:bg-red-50"
          data-testid={`permanent-${item.id}`}
        >
          <Trash2 size={14} className="mr-1" />
          Borrar ya
        </Button>
      </div>
    </li>
  );
};

const TrashList = ({ items, type, onRestore, onPermanent }) => {
  if (!items.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Trash2 size={40} className="mx-auto mb-3 opacity-40" />
        <p>La papelera está vacía</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {items.map(item => (
        <TrashItem
          key={item.id}
          item={item}
          type={type}
          onRestore={onRestore}
          onPermanent={onPermanent}
        />
      ))}
    </ul>
  );
};

const TrashSection = () => {
  const [raffles, setRaffles] = useState([]);
  const [sorteos, setSorteos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${API}/raffles/trash`),
          axios.get(`${API}/sorteos/trash`),
        ]);
        if (cancelled) return;
        setRaffles(r.data || []);
        setSorteos(s.data || []);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          toast.error('Error al cargar la papelera');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRestore = async (type, id) => {
    try {
      await axios.post(`${API}/${type}/${id}/restore`);
      toast.success(type === 'raffles' ? 'Rifa restaurada' : 'Sorteo restaurado');
      if (type === 'raffles') {
        setRaffles(prev => prev.filter(r => r.id !== id));
      } else {
        setSorteos(prev => prev.filter(s => s.id !== id));
      }
    } catch {
      toast.error('Error al restaurar');
    }
  };

  const handlePermanent = async (type, id, title) => {
    if (!window.confirm(`¿Eliminar definitivamente "${title}"?\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/${type}/${id}/permanent`);
      toast.success('Eliminado definitivamente');
      if (type === 'raffles') {
        setRaffles(prev => prev.filter(r => r.id !== id));
      } else {
        setSorteos(prev => prev.filter(s => s.id !== id));
      }
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Trash2 className="text-red-600" />
          Papelera
        </h2>
        <p className="text-gray-600 text-sm">
          Las rifas y sorteos borrados se conservan 90 días. Después se eliminan definitivamente.
        </p>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4 flex items-start gap-2 text-amber-800 text-sm">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <p>Si querés recuperar algo, restauralo antes de que el contador llegue a 0. Después de eso ya no hay vuelta atrás.</p>
        </CardContent>
      </Card>

      <Tabs defaultValue="raffles">
        <TabsList>
          <TabsTrigger value="raffles" data-testid="tab-trash-raffles">
            <Ticket size={14} className="mr-1" />
            Rifas ({raffles.length})
          </TabsTrigger>
          <TabsTrigger value="sorteos" data-testid="tab-trash-sorteos">
            <Gift size={14} className="mr-1" />
            Sorteos ({sorteos.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="raffles">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rifas en papelera</CardTitle>
              <CardDescription>Borradas en los últimos 90 días</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div> : <TrashList items={raffles} type="raffles" onRestore={handleRestore} onPermanent={handlePermanent} />}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="sorteos">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sorteos en papelera</CardTitle>
              <CardDescription>Borrados en los últimos 90 días</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div> : <TrashList items={sorteos} type="sorteos" onRestore={handleRestore} onPermanent={handlePermanent} />}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrashSection;
